<script setup lang="ts" generic="T extends string | number">
import { Icon, type IconName } from '@/shared/ui/icon'

type ChoiceOption = {
    value: T
    label: string
    description?: string
    /** The `choice` card's pictogram before the title. */
    icon?: IconName
    disabled?: boolean
}
const props = withDefaults(
    defineProps<{
        modelValue: T
        options: readonly ChoiceOption[]
        name: string
        label: string
        /** `segmented` — one-line switch; `choice` — cards with a title and a description (design Choice). */
        variant: 'segmented' | 'choice'
        /** Высота сегментов: md 38 (по умолчанию) или sm 32. */
        size?: 'md' | 'sm'
        /** Segments share the whole width equally. */
        stretch?: boolean
        /** `segmented` на узком экране (≤ 640): на всю ширину, по сегменту в строке. */
        stackOnMobile?: boolean
        /** The legend stays for assistive technology only (always so for `segmented`). */
        hideLabel?: boolean
        disabled?: boolean
    }>(),
    { size: 'md' },
)
const emit = defineEmits<{ 'update:modelValue': [value: T] }>()
defineSlots<{
    option?: (props: { option: ChoiceOption; selected: boolean }) => unknown
}>()
function choose(value: T) {
    if (value !== props.modelValue) emit('update:modelValue', value)
}
</script>
<template>
    <fieldset
        class="hb-choice-group"
        :class="[
            `hb-choice-group--${variant}`,
            {
                'hb-choice-group--sm': size === 'sm',
                'hb-choice-group--stretch': stretch,
                'hb-choice-group--stack-mobile': stackOnMobile,
                'hb-choice-group--hide-label': hideLabel,
            },
        ]"
        :disabled="disabled">
        <legend>{{ label }}</legend>
        <label
            v-for="option in options"
            :key="String(option.value)"
            class="hb-choice-option"
            :class="{
                'hb-choice-option--selected': Object.is(option.value, modelValue),
                'hb-choice-option--disabled': option.disabled,
            }"
            ><input
                type="radio"
                :name="name"
                :value="option.value"
                :checked="Object.is(option.value, modelValue)"
                :disabled="option.disabled"
                @change="choose(option.value)" /><span
                ><slot
                    name="option"
                    :option="option"
                    :selected="Object.is(option.value, modelValue)"
                    ><template v-if="variant === 'choice'"
                        ><Icon v-if="option.icon" :name="option.icon" size="sm" /><span
                            class="hb-choice-option__text"
                            ><span class="hb-choice-option__title">{{ option.label }}</span
                            ><span v-if="option.description" class="hb-choice-option__desc">{{
                                option.description
                            }}</span></span
                        ></template
                    ><template v-else
                        >{{ option.label
                        }}<small v-if="option.description">{{
                            option.description
                        }}</small></template
                    ></slot
                ></span
            ></label
        >
    </fieldset>
</template>
